// src/utils/forgeJwe.ts
import forge from 'node-forge';

export type HybridEncryptedPayload = {
  alg: string;       
  enc: string;       
  encryptedKey: string; 
  iv: string;           
  ciphertext: string;   
  tag: string;          
};


export async function encryptWithPublicPem<T>(
  payload: T,
  publicKeyPem: string
): Promise<HybridEncryptedPayload> {
  const pem = (publicKeyPem || '').trim();
  if (!pem.includes('-----BEGIN') || !pem.includes('-----END')) {
    throw new Error('Invalid PEM public key');
  }
  const cleanPem = pem; // pass-through; forge handles whitespace/newlines

  const dataString = typeof payload === 'string' ? payload : JSON.stringify(payload);
  const dataBytes = forge.util.encodeUtf8(dataString);

  const cekBytes = forge.random.getBytesSync(32);
  const ivBytes = forge.random.getBytesSync(12);

  const cipher = forge.cipher.createCipher('AES-GCM', cekBytes);
  cipher.start({
    iv: ivBytes,
    additionalData: '',
    tagLength: 128
  });
  cipher.update(forge.util.createBuffer(dataBytes, 'raw'));
  cipher.finish();
  const ciphertextBytes = cipher.output.getBytes();
  const tagBytes = cipher.mode.tag.getBytes();

  let pubKey;
  try {
    pubKey = forge.pki.publicKeyFromPem(cleanPem);
  } catch (err) {
    throw new Error('Failed to parse public key PEM: ' + String(err));
  }

  const encryptedCekBytes = pubKey.encrypt(cekBytes, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: { md: forge.md.sha256.create() }
  });

  const b64 = (bytes: string) => forge.util.encode64(bytes);

  return {
    alg: 'RSA-OAEP-256',
    enc: 'A256GCM',
    encryptedKey: b64(encryptedCekBytes),
    iv: b64(ivBytes),
    ciphertext: b64(ciphertextBytes),
    tag: b64(tagBytes)
  };
}